import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CategoryKind,
  LoanKind,
  TransactionType,
  loanProgress,
  loanRemainingBalance,
  nextBusinessMonthDate,
  termsRemaining,
  totalMonthlyLoanPayments,
  type AppCurrency,
  type Loan,
} from '@financial/shared';
import {
  useCreateLoan,
  useDeleteLoan,
  useLoans,
  usePayLoan,
  useUpdateLoan,
} from '../features/loans/useLoans';
import { useAccounts } from '../features/accounts/useAccounts';
import { useCategories } from '../features/categories/useCategories';
import {
  currentCurrency,
  formatCalendarDate,
  formatMoney,
  toCalendarDateInputValue,
  toDateInputValue,
} from '../lib/format';
import { accountAvailableBalance, transactionBalanceChange } from '../lib/balancePreview';
import { getApiErrorMessage } from '../lib/errors';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { DatePicker } from '../components/ui/DatePicker';
import { Field } from '../components/ui/Field';
import { Modal } from '../components/ui/Modal';
import { ItemActionsMenu } from '../components/ui/ItemActionsMenu';
import {
  Button,
  EmptyState,
  FilterChipGroup,
  HelperText,
  Skeleton,
} from '../components/ui/Primitives';
import { SkeletonState } from '../components/ui/SkeletonState';
import { GuidedPageHeader } from '../components/onboarding/GuidedPageHeader';

function createPaymentAttemptKey(): string {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  return `loan-payment-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isValidDateInput(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day, 12));
  return parsed.toISOString().slice(0, 10) === value;
}

function nextDueDateAfterPayment(loan: Loan): string | undefined {
  if (loan.termsPaid + 1 >= loan.termsTotal || !loan.nextDueDate) return undefined;
  return nextBusinessMonthDate(loan.nextDueDate).toISOString();
}

export function LoansPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const listQ = useLoans();
  const deleteLoan = useDeleteLoan();
  const currency = currentCurrency();
  const [addOpen, setAddOpen] = useState(false);
  const [loanToEdit, setLoanToEdit] = useState<Loan | null>(null);
  const [loanToPay, setLoanToPay] = useState<Loan | null>(null);
  const [loanToDelete, setLoanToDelete] = useState<Loan | null>(null);
  const [status, setStatus] = useState<'active' | 'completed' | 'all'>('active');
  const loans = useMemo(
    () => (listQ.data ?? []).filter((loan) => loan.currency === currency),
    [currency, listQ.data],
  );
  const activeLoans = useMemo(() => loans.filter((loan) => termsRemaining(loan) > 0), [loans]);
  const activeDebts = useMemo(
    () => activeLoans.filter((loan) => loan.kind === LoanKind.DEBT),
    [activeLoans],
  );
  const activeReceivables = useMemo(
    () => activeLoans.filter((loan) => loan.kind === LoanKind.LOAN),
    [activeLoans],
  );
  const visibleLoans = useMemo(() => {
    if (status === 'active') return activeLoans;
    if (status === 'completed') return loans.filter((loan) => termsRemaining(loan) === 0);
    return loans;
  }, [activeLoans, loans, status]);
  const totalDebt = activeDebts.reduce((sum, loan) => sum + loanRemainingBalance(loan), 0);
  const totalDebtMonthly = totalMonthlyLoanPayments(activeDebts);
  const totalReceivable = activeReceivables.reduce(
    (sum, loan) => sum + loanRemainingBalance(loan),
    0,
  );
  const totalReceivableMonthly = totalMonthlyLoanPayments(activeReceivables);

  useEffect(() => {
    if (searchParams.get('nuevo') !== '1') return;
    setAddOpen(true);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('nuevo');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  return (
    <div className="reference-page w-full">
      <GuidedPageHeader
        section="loans"
        title="Deudas y préstamos"
        description="Separa lo que debes de lo que te deben y revisa cuándo viene el siguiente pago o cobro."
        actions={<Button onClick={() => setAddOpen(true)}>+ Deuda o préstamo</Button>}
      />

      <div>
        <div className="system-page mx-auto max-w-[1180px]">
          {loans.length > 0 && (
            <div className="mb-4">
              <p className="mb-2 text-xs font-semibold text-slate-600">
                Resumen en {currency} · la app no convierte importes de otras monedas
              </p>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <SummaryCard
                  label="Debes"
                  value={totalDebt}
                  currency={currency}
                  detail="Saldo estimado por pagar"
                  tone="expense"
                />
                <SummaryCard
                  label="Pago mensual"
                  value={totalDebtMonthly}
                  currency={currency}
                  detail="Sólo deudas activas"
                  tone="expense"
                />
                <SummaryCard
                  label="Te deben"
                  value={totalReceivable}
                  currency={currency}
                  detail="Saldo estimado por cobrar"
                  tone="income"
                />
                <SummaryCard
                  label="Cobro mensual"
                  value={totalReceivableMonthly}
                  currency={currency}
                  detail="Esperado de préstamos"
                  tone="income"
                />
              </div>
            </div>
          )}

          {loans.length > 0 && (
            <FilterChipGroup
              className="mb-4"
              value={status}
              ariaLabel="Filtrar deudas por estado"
              options={[
                { value: 'active' as const, label: 'Activas' },
                { value: 'completed' as const, label: 'Liquidadas' },
                { value: 'all' as const, label: 'Todas' },
              ]}
              onChange={setStatus}
            />
          )}

          {listQ.isLoading && <LoansSkeleton />}
          {listQ.isError && (
            <div className="ui-card text-center" role="alert">
              <p className="font-semibold text-slate-900">No pudimos cargar tus deudas</p>
              <p className="mt-1 text-sm text-slate-600">
                Vuelve a intentarlo antes de registrar o cambiar información.
              </p>
              <Button className="mt-4" onClick={() => void listQ.refetch()}>
                Volver a intentar
              </Button>
            </div>
          )}
          {loans.length === 0 && !listQ.isLoading && !listQ.isError && (
            <EmptyState
              title="Sin deudas ni préstamos"
              description={`No hay registros en ${currency}. Agrega una deuda para incluir su pago mensual en el presupuesto o un préstamo para seguir lo que te deben.`}
              action={<Button onClick={() => setAddOpen(true)}>Agregar el primero</Button>}
            />
          )}

          {loans.length > 0 && visibleLoans.length === 0 && !listQ.isLoading && (
            <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-600">
              No hay deudas ni préstamos en este estado.
            </p>
          )}
          {activeLoans.length > 0 && (
            <HelperText className="mb-4" tone="info">
              Antes de marcar un pago o cobro verás qué cambia. También podrás registrar la salida o
              entrada de dinero en una cuenta, sin capturarla dos veces.
            </HelperText>
          )}
          <ul className="space-y-3">
            {visibleLoans.map((loan) => (
              <LoanCard
                key={loan.id}
                loan={loan}
                onEdit={() => setLoanToEdit(loan)}
                onPay={() => setLoanToPay(loan)}
                onDelete={() => setLoanToDelete(loan)}
              />
            ))}
          </ul>

          <Modal
            open={addOpen}
            title="Nueva deuda o préstamo"
            size="wide"
            onClose={() => setAddOpen(false)}
          >
            <LoanForm onDone={() => setAddOpen(false)} />
          </Modal>

          <Modal
            open={loanToEdit !== null}
            title={loanToEdit?.kind === LoanKind.LOAN ? 'Editar préstamo' : 'Editar deuda'}
            eyebrow={loanToEdit?.name}
            size="wide"
            onClose={() => setLoanToEdit(null)}
          >
            {loanToEdit && (
              <LoanForm key={loanToEdit.id} loan={loanToEdit} onDone={() => setLoanToEdit(null)} />
            )}
          </Modal>

          <Modal
            open={loanToPay !== null}
            title={loanToPay?.kind === LoanKind.LOAN ? 'Marcar cobro' : 'Marcar pago'}
            eyebrow={loanToPay?.name}
            size="wide"
            onClose={() => setLoanToPay(null)}
          >
            {loanToPay && (
              <LoanPaymentForm
                key={loanToPay.id}
                loan={loanToPay}
                onDone={() => setLoanToPay(null)}
              />
            )}
          </Modal>

          <ConfirmDialog
            open={loanToDelete !== null}
            title={
              loanToDelete?.kind === LoanKind.LOAN
                ? '¿Eliminar este préstamo?'
                : '¿Eliminar esta deuda?'
            }
            description="Se quitarán su calendario y saldo estimado sólo si todavía no tiene historial creado con “Marcar pago” o “Marcar cobro”. Un registro con historial debe conservarse para mantenerlo conciliado."
            itemLabel={
              loanToDelete
                ? `${loanToDelete.name} · ${formatMoney(
                    loanRemainingBalance(loanToDelete),
                    loanToDelete.currency,
                  )}`
                : undefined
            }
            pending={deleteLoan.isPending}
            error={deleteLoan.isError ? getApiErrorMessage(deleteLoan.error) : undefined}
            onClose={() => {
              if (!deleteLoan.isPending) setLoanToDelete(null);
            }}
            onConfirm={() =>
              loanToDelete &&
              deleteLoan.mutate(loanToDelete.id, { onSuccess: () => setLoanToDelete(null) })
            }
          />
        </div>
      </div>
    </div>
  );
}

export function LoansSkeleton() {
  return (
    <SkeletonState label="Cargando deudas y préstamos…" className="space-y-3">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-24" />
        ))}
      </div>
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton key={index} className="h-36 w-full" />
      ))}
    </SkeletonState>
  );
}

function SummaryCard({
  label,
  value,
  currency,
  detail,
  tone,
}: {
  label: string;
  value: number;
  currency: AppCurrency;
  detail: string;
  tone: 'expense' | 'income';
}) {
  return (
    <div className="ui-card p-4">
      <p className="text-xs text-slate-600">{label}</p>
      <p className={`text-2xl font-bold ${tone === 'expense' ? 'text-expense' : 'text-income'}`}>
        {formatMoney(value, currency)}
      </p>
      <p className="mt-1 text-xs text-slate-600">{detail}</p>
    </div>
  );
}

function LoanCard({
  loan,
  onEdit,
  onPay,
  onDelete,
}: {
  loan: Loan;
  onEdit: () => void;
  onPay: () => void;
  onDelete: () => void;
}) {
  const progress = Math.round(loanProgress(loan) * 100);
  const remaining = termsRemaining(loan);
  const isDebt = loan.kind === LoanKind.DEBT;

  return (
    <li className="ui-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">
            {loan.name}{' '}
            <span className={`text-xs font-semibold ${isDebt ? 'text-expense' : 'text-income'}`}>
              {isDebt ? 'deuda' : 'por cobrar'}
            </span>
          </p>
          <p className="mt-1 text-xs leading-5 text-slate-600">
            {formatMoney(loan.monthlyPayment, loan.currency)}/mes · {loan.termsPaid}/
            {loan.termsTotal} {isDebt ? 'pagos' : 'cobros'}
            {loan.nextDueDate
              ? ` · próx. ${formatCalendarDate(loan.nextDueDate)}`
              : ' · sin próxima fecha'}
          </p>
        </div>
        <p
          className={`shrink-0 text-right text-lg font-bold ${isDebt ? 'text-expense' : 'text-income'}`}
        >
          {formatMoney(loanRemainingBalance(loan), loan.currency)}
          <span className="mt-0.5 block text-xs font-medium text-slate-600">
            {isDebt ? 'por pagar (estimado)' : 'por cobrar (estimado)'}
          </span>
        </p>
      </div>

      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-savings" style={{ width: `${progress}%` }} />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-slate-600">
          Faltan {remaining} {isDebt ? 'pagos' : 'cobros'}
        </span>
        <div className="flex shrink-0 items-center justify-end gap-1">
          {remaining > 0 && (
            <Button variant="secondary" className="px-3" onClick={onPay}>
              {isDebt ? 'Marcar pago' : 'Marcar cobro'}
            </Button>
          )}
          <ItemActionsMenu
            itemLabel={loan.name}
            title={isDebt ? 'Opciones de la deuda' : 'Opciones del préstamo'}
            actions={[
              {
                label: isDebt ? 'Editar deuda' : 'Editar préstamo',
                description: 'Cambia el nombre, monto, plazo o próxima fecha.',
                onSelect: onEdit,
              },
              {
                label: isDebt ? 'Eliminar deuda' : 'Eliminar préstamo',
                description: 'Disponible sólo si todavía no tiene historial de pagos.',
                tone: 'danger',
                onSelect: onDelete,
              },
            ]}
          />
        </div>
      </div>
    </li>
  );
}

function LoanForm({ loan, onDone }: { loan?: Loan; onDone: () => void }) {
  const create = useCreateLoan();
  const update = useUpdateLoan();
  const [name, setName] = useState(loan?.name ?? '');
  const [kind, setKind] = useState<LoanKind>(loan?.kind ?? LoanKind.DEBT);
  const [monthlyPayment, setMonthlyPayment] = useState(loan ? String(loan.monthlyPayment) : '');
  const [principal, setPrincipal] = useState(loan ? String(loan.principal) : '');
  const [termsTotal, setTermsTotal] = useState(loan ? String(loan.termsTotal) : '12');
  const [termsPaid, setTermsPaid] = useState(loan ? String(loan.termsPaid) : '0');
  const [nextDueDate, setNextDueDate] = useState(
    loan?.nextDueDate ? toCalendarDateInputValue(loan.nextDueDate) : '',
  );
  const currency = loan?.currency ?? currentCurrency();
  const monthlyAmount = Number(monthlyPayment);
  const originalAmount = principal.trim() === '' ? 0 : Number(principal);
  const totalPayments = Number(termsTotal);
  const paidPayments = Number(termsPaid);
  const monthlyAmountIsValid =
    monthlyPayment.trim() !== '' && Number.isFinite(monthlyAmount) && monthlyAmount > 0;
  const originalAmountIsValid = Number.isFinite(originalAmount) && originalAmount >= 0;
  const totalPaymentsIsValid = Number.isInteger(totalPayments) && totalPayments >= 1;
  const paidPaymentsIsValid =
    Number.isInteger(paidPayments) &&
    paidPayments >= 0 &&
    totalPaymentsIsValid &&
    paidPayments <= totalPayments;
  const nextDueDateIsValid = nextDueDate === '' || isValidDateInput(nextDueDate);
  const isLiquidated = paidPaymentsIsValid && paidPayments === totalPayments;
  const pending = create.isPending || update.isPending;
  const mutationError = create.error ?? update.error;
  const formIsValid =
    name.trim() !== '' &&
    monthlyAmountIsValid &&
    originalAmountIsValid &&
    totalPaymentsIsValid &&
    paidPaymentsIsValid &&
    nextDueDateIsValid;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!formIsValid) return;
    if (loan) {
      update.mutate(
        {
          id: loan.id,
          body: {
            name: name.trim(),
            principal: originalAmount,
            monthlyPayment: monthlyAmount,
            termsTotal: totalPayments,
            termsPaid: paidPayments,
            nextDueDate: isLiquidated ? null : nextDueDate || null,
          },
        },
        { onSuccess: onDone },
      );
      return;
    }
    create.mutate(
      {
        name: name.trim(),
        kind,
        currency,
        principal: originalAmount,
        monthlyPayment: monthlyAmount,
        termsTotal: totalPayments,
        termsPaid: paidPayments,
        nextDueDate: isLiquidated ? undefined : nextDueDate || undefined,
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {loan ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
          <span className="font-semibold">{kind === LoanKind.DEBT ? 'Deuda' : 'Préstamo'}</span>
          <span className="text-slate-500"> · {currency}</span>
          <p className="mt-1 text-xs leading-5 text-slate-600">
            El tipo y la moneda no cambian al editar para conservar sus cálculos anteriores.
          </p>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-form-value-change={kind === LoanKind.DEBT ? undefined : 'true'}
              onClick={() => setKind(LoanKind.DEBT)}
              className={`tap-target rounded-lg py-2 text-sm font-semibold ${
                kind === LoanKind.DEBT ? 'bg-expense text-white' : 'bg-slate-100 text-slate-600'
              }`}
            >
              Deuda (debo)
            </button>
            <button
              type="button"
              data-form-value-change={kind === LoanKind.LOAN ? undefined : 'true'}
              onClick={() => setKind(LoanKind.LOAN)}
              className={`tap-target rounded-lg py-2 text-sm font-semibold ${
                kind === LoanKind.LOAN ? 'bg-income text-white' : 'bg-slate-100 text-slate-600'
              }`}
            >
              Préstamo (me deben)
            </button>
          </div>
          <p className="mt-2 text-xs leading-5 text-slate-600">
            {kind === LoanKind.DEBT
              ? 'La mensualidad se contará como un pago que debes cubrir cada mes.'
              : 'El préstamo se mostrará como dinero por cobrar; no aumenta el saldo de una cuenta hasta que registres el cobro.'}
          </p>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="lg:col-span-2">
          <Field
            id={loan ? 'edit-loan-name' : 'new-loan-name'}
            label="¿Cómo quieres reconocerlo?"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={kind === LoanKind.DEBT ? 'Ej. Crédito del auto' : 'Ej. Préstamo familiar'}
          />
        </div>
        <Field
          id={loan ? 'edit-loan-monthly' : 'new-loan-monthly'}
          label={kind === LoanKind.DEBT ? 'Pago mensual' : 'Cobro mensual'}
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0.01"
          required
          value={monthlyPayment}
          onChange={(event) => setMonthlyPayment(event.target.value)}
          helperText={
            kind === LoanKind.DEBT
              ? 'Importe que normalmente pagas cada mes.'
              : 'Importe que normalmente esperas cobrar cada mes.'
          }
        />
        <Field
          id={loan ? 'edit-loan-principal' : 'new-loan-principal'}
          label="Monto original (opcional)"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={principal}
          onChange={(event) => setPrincipal(event.target.value)}
          helperText="Se guarda como referencia. El saldo estimado usa la mensualidad y los pagos que faltan."
        />
        <Field
          id={loan ? 'edit-loan-total' : 'new-loan-total'}
          label={kind === LoanKind.DEBT ? 'Total de pagos' : 'Total de cobros'}
          type="number"
          inputMode="numeric"
          min="1"
          step="1"
          required
          value={termsTotal}
          onChange={(event) => setTermsTotal(event.target.value)}
        />
        <Field
          id={loan ? 'edit-loan-paid' : 'new-loan-paid'}
          label={kind === LoanKind.DEBT ? 'Pagos hechos' : 'Cobros recibidos'}
          type="number"
          inputMode="numeric"
          min="0"
          max={totalPaymentsIsValid ? totalPayments : undefined}
          step="1"
          required
          value={termsPaid}
          onChange={(event) => setTermsPaid(event.target.value)}
        />
        <div className="lg:col-span-2">
          {isLiquidated ? (
            <HelperText tone="success">
              Al estar liquidado no necesita próxima fecha. Guardaremos el calendario sin un pago o
              cobro pendiente.
            </HelperText>
          ) : (
            <>
              <DatePicker
                id={loan ? 'edit-loan-next-date' : 'new-loan-next-date'}
                label={
                  kind === LoanKind.DEBT ? 'Próximo pago (opcional)' : 'Próximo cobro (opcional)'
                }
                value={nextDueDate}
                onChange={setNextDueDate}
                helperText="La fecha permite mostrar este compromiso en Panorama. No mueve dinero por sí sola."
              />
              {!nextDueDate && (
                <p className="mt-2 text-xs leading-5 text-amber-800">
                  Sin fecha no aparecerá en “Próximos compromisos”. Puedes agregarla después al
                  editar.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {!paidPaymentsIsValid && termsPaid.trim() !== '' && (
        <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-expense">
          Los {kind === LoanKind.DEBT ? 'pagos hechos' : 'cobros recibidos'} deben ser un número
          entero entre 0 y el total.
        </p>
      )}
      {!originalAmountIsValid && (
        <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-expense">
          El monto original no puede ser negativo.
        </p>
      )}
      {mutationError && (
        <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-expense">
          {getApiErrorMessage(mutationError)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={pending || !formIsValid}
          className="button button-primary w-full text-base"
        >
          {pending
            ? 'Guardando…'
            : loan
              ? 'Guardar cambios'
              : kind === LoanKind.DEBT
                ? 'Guardar deuda'
                : 'Guardar préstamo'}
        </button>
      </div>
    </form>
  );
}

function BalanceChangeRow({
  label,
  before,
  after,
  currency,
}: {
  label: string;
  before: number;
  after: number;
  currency: AppCurrency;
}) {
  const beforeLabel = formatMoney(before, currency);
  const afterLabel = formatMoney(after, currency);
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-600">{label}</dt>
      <dd className="shrink-0 font-semibold tabular-nums text-slate-900">
        <span className="sr-only">
          Pasa de {beforeLabel} a {afterLabel}
        </span>
        <span aria-hidden className="flex items-center gap-1.5">
          <span>{beforeLabel}</span>
          <span className="text-slate-500">→</span>
          <span>{afterLabel}</span>
        </span>
      </dd>
    </div>
  );
}

function LoanPaymentForm({ loan, onDone }: { loan: Loan; onDone: () => void }) {
  const accountsQ = useAccounts();
  const categoriesQ = useCategories();
  const pay = usePayLoan();
  const isDebt = loan.kind === LoanKind.DEBT;
  const transactionType = isDebt ? TransactionType.EXPENSE : TransactionType.INCOME;
  const categoryKind = isDebt ? CategoryKind.EXPENSE : CategoryKind.INCOME;
  const [date, setDate] = useState(toDateInputValue());
  const [registerMovement, setRegisterMovement] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [idempotencyKey] = useState(createPaymentAttemptKey);
  const accounts = (accountsQ.data ?? []).filter((account) => account.currency === loan.currency);
  const categories = (categoriesQ.data ?? []).filter((category) => category.kind === categoryKind);
  const selectedAccount = accounts.find((account) => account.id === accountId);
  const selectedAvailable = selectedAccount ? accountAvailableBalance(selectedAccount) : 0;
  const amountTooLarge =
    isDebt && Boolean(selectedAccount) && loan.monthlyPayment > selectedAvailable + 0.005;
  const accountChange = selectedAccount
    ? transactionBalanceChange(selectedAccount, transactionType, loan.monthlyPayment)
    : null;
  const today = toDateInputValue();
  const dateIsValid = isValidDateInput(date) && date <= today;
  const resultingTermsPaid = Math.min(loan.termsTotal, loan.termsPaid + 1);
  const resultingBalance = loan.monthlyPayment * Math.max(0, loan.termsTotal - resultingTermsPaid);
  const followingDueDate = nextDueDateAfterPayment(loan);
  const canSubmit =
    dateIsValid &&
    (!registerMovement || (Boolean(selectedAccount) && !amountTooLarge)) &&
    !pay.isPending;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    pay.mutate(
      {
        loanId: loan.id,
        body: {
          date,
          registerMovement,
          accountId: registerMovement ? accountId : undefined,
          categoryId: registerMovement && categoryId ? categoryId : undefined,
          idempotencyKey,
        },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <HelperText tone="info">
        Marcarlo actualiza los {isDebt ? 'pagos hechos' : 'cobros recibidos'} y la próxima fecha.
        Sólo cambiará una cuenta si activas el registro del movimiento.
      </HelperText>

      <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
        <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4" aria-live="polite">
          <p className="text-xs font-bold uppercase tracking-[.08em] text-slate-600">
            Revisa antes de guardar
          </p>
          <h3 className="mt-1 text-lg font-bold text-slate-900">
            {isDebt ? 'Pago' : 'Cobro'} {resultingTermsPaid} de {loan.termsTotal}
          </h3>
          <dl className="mt-4 space-y-3 border-t border-slate-200 pt-4 text-sm">
            <BalanceChangeRow
              label={isDebt ? 'Saldo estimado por pagar' : 'Saldo estimado por cobrar'}
              before={loanRemainingBalance(loan)}
              after={resultingBalance}
              currency={loan.currency}
            />
            <div className="flex items-start justify-between gap-3">
              <dt className="text-slate-600">Calendario</dt>
              <dd className="text-right font-semibold text-slate-900">
                {loan.nextDueDate ? formatCalendarDate(loan.nextDueDate) : 'Sin fecha'}
                <span className="mx-1.5 text-slate-500">→</span>
                {resultingTermsPaid >= loan.termsTotal
                  ? 'Liquidado'
                  : followingDueDate
                    ? formatCalendarDate(followingDueDate)
                    : 'Sin fecha'}
              </dd>
            </div>
          </dl>
          <p className="mt-4 text-xs leading-5 text-slate-600">
            El saldo es estimado: mensualidad × {isDebt ? 'pagos' : 'cobros'} pendientes. No calcula
            intereses ni otros costos.
          </p>
        </section>

        <div className="space-y-4">
          <DatePicker
            id="loan-payment-date"
            label={isDebt ? '¿Cuándo hiciste el pago?' : '¿Cuándo recibiste el cobro?'}
            required
            max={today}
            value={date}
            onChange={setDate}
            helperText="Es la fecha en que ocurrió, no la siguiente fecha del calendario."
          />
          {!dateIsValid && (
            <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-expense">
              Elige una fecha válida que no esté en el futuro.
            </p>
          )}

          <label className="flex min-h-11 cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white p-3">
            <input
              type="checkbox"
              checked={registerMovement}
              onChange={(event) => setRegisterMovement(event.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 accent-slate-900"
            />
            <span>
              <span className="block text-sm font-semibold text-slate-800">
                {isDebt
                  ? 'Registrar también la salida de dinero'
                  : 'Registrar también el dinero recibido'}
              </span>
              <span className="mt-1 block text-xs leading-5 text-slate-600">
                Actívalo si el dinero ya {isDebt ? 'salió de' : 'entró a'} una cuenta. Deudas lo
                clasificará para que no vuelva a contarse como {isDebt ? 'gasto' : 'ingreso'}. Si ya
                lo capturaste manualmente en Movimientos, cancela, elimina aquel movimiento y vuelve
                aquí.
              </span>
            </span>
          </label>
          {!registerMovement && (
            <HelperText tone="warning">
              Así sólo se actualizarán el avance y la próxima fecha. La app no reclasificará un
              movimiento que hayas capturado antes por separado.
            </HelperText>
          )}
        </div>
      </div>

      {registerMovement && (
        <section className="rounded-2xl border border-blue-200 bg-blue-50/60 p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.08em] text-blue-700">
                Movimiento en cuenta
              </p>
              <h3 className="mt-1 font-bold text-slate-900">
                {isDebt ? '¿De dónde salió el dinero?' : '¿Dónde recibiste el dinero?'}
              </h3>
            </div>
            <span className="rounded-md bg-white px-2 py-1 text-xs font-bold text-slate-600">
              {loan.currency}
            </span>
          </div>

          {accountsQ.isLoading ? (
            <SkeletonState
              label={`Cargando cuentas en ${loan.currency}…`}
              className="mt-4 grid gap-4 lg:grid-cols-2"
            >
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </SkeletonState>
          ) : accounts.length > 0 ? (
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div>
                <label
                  htmlFor="loan-payment-account"
                  className="mb-1.5 block text-sm font-semibold text-slate-700"
                >
                  Cuenta
                </label>
                <select
                  id="loan-payment-account"
                  required={registerMovement}
                  value={accountId}
                  onChange={(event) => setAccountId(event.target.value)}
                  className="tap-target h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                >
                  <option value="" disabled>
                    Elige una cuenta en {loan.currency}
                  </option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="loan-payment-category"
                  className="mb-1.5 block text-sm font-semibold text-slate-700"
                >
                  Categoría (opcional)
                </label>
                <select
                  id="loan-payment-category"
                  value={categoryId}
                  onChange={(event) => setCategoryId(event.target.value)}
                  className="tap-target h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                >
                  <option value="">Sin categoría (revisar después)</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.icon ? `${category.icon} ` : ''}
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : !accountsQ.isError ? (
            <HelperText className="mt-4" tone="warning">
              No tienes una cuenta en {loan.currency}. Puedes marcar el {isDebt ? 'pago' : 'cobro'}
              sin movimiento o agregar primero una cuenta compatible.
            </HelperText>
          ) : null}

          {accountChange && (
            <div className="mt-4 rounded-xl border border-blue-100 bg-white p-3" aria-live="polite">
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-sm font-bold text-slate-900">
                  Así quedará {selectedAccount?.name}
                </h4>
                <span className="text-xs font-semibold text-slate-600">Registrado</span>
              </div>
              <dl className="mt-3 space-y-2 border-t border-slate-100 pt-3 text-sm">
                <BalanceChangeRow
                  label={selectedAccount?.buckets.length ? 'Saldo total' : 'Saldo de la cuenta'}
                  before={accountChange.total.before}
                  after={accountChange.total.after}
                  currency={loan.currency}
                />
                {Boolean(selectedAccount?.buckets.length) && (
                  <BalanceChangeRow
                    label="Disponible fuera de apartados"
                    before={accountChange.available.before}
                    after={accountChange.available.after}
                    currency={loan.currency}
                  />
                )}
              </dl>
            </div>
          )}

          {amountTooLarge && selectedAccount && (
            <p role="alert" className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-expense">
              {selectedAccount.name} sólo tiene {formatMoney(selectedAvailable, loan.currency)}
              disponible fuera de apartados. Elige otra cuenta o registra el pago sin movimiento.
            </p>
          )}
          {accountsQ.isError && (
            <p role="alert" className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-expense">
              No pudimos consultar tus cuentas. Marca el {isDebt ? 'pago' : 'cobro'} sin movimiento
              o vuelve a intentarlo más tarde.
            </p>
          )}
        </section>
      )}

      {pay.isError && (
        <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-expense">
          {getApiErrorMessage(pay.error)}
        </p>
      )}

      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={!canSubmit}
          className="button button-primary w-full text-base"
        >
          {pay.isPending
            ? 'Guardando…'
            : registerMovement
              ? isDebt
                ? 'Marcar pago y registrar movimiento'
                : 'Marcar cobro y registrar movimiento'
              : isDebt
                ? 'Marcar pago'
                : 'Marcar cobro'}
        </button>
      </div>
    </form>
  );
}
